import { useCallback, useMemo } from 'react';
import { useSingleForm } from '../../hooks';
import { classNames } from '../../utils/classNames';
import {
  useCommentFormFileUpload,
  getActionBarConfigWithFileUpload,
  getFileUploadInputProps
} from './useCommentFormFileUpload';
import type {
  CommentFormProps,
  CommentSubmissionData
} from './CommentForm.types';
import type { CommentMention } from '../CommentList/CommentList.types';

export function useCommentForm(
  props: Omit<CommentFormProps, 'children'> & { disabled?: boolean }
) {
  const {
    inputConfig,
    labelConfig = {},
    onSubmit,
    className,
    disabled
  } = props;

  const {
    currentValue,
    mentions,
    handleValueChange,
    handleMentionsChange,
    clearValue
  } = useSingleForm(
    '',
    inputConfig?.value !== undefined,
    inputConfig?.value,
    inputConfig?.onChange as (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => void
  );

  const fileUpload = useCommentFormFileUpload(
    inputConfig?.actionBarConfig,
    clearValue
  );

  const formId = useMemo(
    () => inputConfig?.id || 'comment-form',
    [inputConfig?.id]
  );
  const inputId = useMemo(() => `${formId}-input`, [formId]);

  const submitComment = useCallback(() => {
    const trimmedValue = (currentValue || '').trim();
    const hasAttachments = fileUpload ? fileUpload.attachmentCount > 0 : false;
    if (!onSubmit || (!trimmedValue && !hasAttachments)) return;

    const submissionData: CommentSubmissionData = {
      comment: trimmedValue,
      commentMentions: mentions,
      ...(hasAttachments && fileUpload
        ? { attachments: fileUpload.getFiles() }
        : {})
    };
    onSubmit(submissionData);
    clearValue();
    fileUpload?.resetAfterSubmit();
  }, [onSubmit, currentValue, mentions, clearValue, fileUpload]);

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      submitComment();
    },
    [submitComment]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLFormElement>) => {
      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        handleSubmit(event as unknown as React.FormEvent<HTMLFormElement>);
      }
    },
    [handleSubmit]
  );

  const combinedMentionsHandler = useCallback(
    (newMentions: CommentMention[]) => {
      handleMentionsChange(newMentions);
      if (inputConfig?.onMentionsChange) {
        inputConfig.onMentionsChange(newMentions);
      }
    },
    [handleMentionsChange, inputConfig?.onMentionsChange]
  );

  const inputProps = useMemo(
    () => ({
      ...(inputConfig ?? {}),
      id: inputId,
      value: currentValue,
      onChange: handleValueChange,
      onMentionsChange: combinedMentionsHandler,
      onSubmit: () => submitComment(),
      ...(fileUpload ? getFileUploadInputProps(fileUpload) : {}),
      actionBarConfig: fileUpload
        ? getActionBarConfigWithFileUpload(
            inputConfig?.actionBarConfig,
            fileUpload
          )
        : {
            ...inputConfig?.actionBarConfig,
            onCancelClick: clearValue
          }
    }),
    [
      inputConfig,
      inputId,
      currentValue,
      handleValueChange,
      combinedMentionsHandler,
      submitComment,
      clearValue,
      fileUpload
    ]
  );

  const labelProps = useMemo(
    () => ({
      ...labelConfig,
      htmlFor: inputId,
      className: classNames('comment-form__label', labelConfig.className)
    }),
    [labelConfig, inputId]
  );

  const formClassName = useMemo(
    () =>
      classNames(
        'comment-form',
        disabled && 'comment-form--disabled',
        className
      ),
    [disabled, className]
  );

  return {
    formId,
    inputId,
    inputProps,
    labelProps,
    formClassName,
    handleSubmit,
    handleKeyDown,
    ...(fileUpload
      ? {
          fileInputRef: fileUpload.fileInputRef,
          fileUploadConfig: fileUpload.fileUploadConfig,
          handleFileInputChange: fileUpload.handleFileInputChange,
          attachments: fileUpload.attachments,
          previewOpen: fileUpload.previewOpen,
          setPreviewOpen: fileUpload.setPreviewOpen,
          carouselOpen: fileUpload.carouselOpen,
          setCarouselOpen: fileUpload.setCarouselOpen,
          carouselIndex: fileUpload.carouselIndex,
          uploadAreaRef: fileUpload.uploadAreaRef,
          removeFile: fileUpload.removeFile,
          openCarousel: fileUpload.openCarousel
        }
      : {})
  } as const;
}
